/* eslint-disable no-nested-ternary */
import { QuestionCircleOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { message, Tooltip } from 'antd';
import React, {
  useEffect, useMemo, useRef, useState
} from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  FormProvider, useFieldArray, useForm, useWatch
} from 'react-hook-form';
import { useQuery } from 'react-query';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Label, Radio, Typography } from 'tfc-components';

import BillingInformation from './BillingInformation';
import ChoosePackage from './ChoosePackage';
import CleanData from './CleanData';
import Confirmation from './Confirmation';
import FooterChangePlan from './FooterChangePlan';
import PackageSelector from './PackageSelector';

import Icon from 'components/atoms/Icon';
import Section from 'components/organisms/Section';
import { getClientUsageSummary } from 'services/dashboard';
import { changePlanDiscountService } from 'services/overview';
import { OverViewData } from 'services/overview/types';
import { getMainPackagesService } from 'services/servicePackages';
import {
  ServicePackageAddOnData,
  ServicePackageData,
} from 'services/servicePackages/type';
import mapModifiers from 'utils/functions';
import { changePlanBillingInfoSchema } from 'utils/schemas';

export interface PackageOption {
  id: string;
  name: string;
  amount: number;
  duration: number;
  description?: string;
  servicePackageAddOnData?: ServicePackageAddOnData[];
  numberOfAgent: number;
  numberOfFile: number;
  totalSizeLimit: number;
  numberOfWebsite: number;
  numberOfSession: number;
}

export type InvoiceType = 'business' | 'individual';

export interface IBillingInfo {
  fullName: string;
  phone: string;
  email: string;
  invoiceType: InvoiceType;
  invoice: IBusinessInfo | IIndividualInfo;
}

export interface IBusinessInfo {
  taxId: string;
  companyName: string;
  fullAddress: string;
  invoiceEmail?: string[];
  invoiceEmails?: string[];
}
export interface IIndividualInfo {
  nationalId: string;
  fullName: string;
  fullAddress: string;
  invoiceEmail?: string[];
  invoiceEmails?: string[];
}

export type IForm = {
  mainPackage: ServicePackageData | null;
  addOnPackage: ServicePackageAddOnData[];
};

export type InvoiceFormType = {
  billingInfo: IBusinessInfo | IIndividualInfo,
  invoiceType: number
};

export type TClean = {
  needToClean: number;
  cleaned: Record<string, number>; // cleaned theo từng chatbot
  code: Record<string, string[]>; // list file theo từng chatbot
};

export type TCleanRequire = {
  chatbot: TClean;
  storage: TClean;
  file: TClean;
  web: TClean;
};

const ChangePlan: React.FC = () => {
  const location = useLocation() as {
    state: OverViewData & {
      dataRetry: any
    }
  };

  const [step, setStep] = useState(1);
  const [searchParams] = useSearchParams();
  const planName = searchParams.get('planName');
  const [billingType, setBillingType] = useState<'one-time' | 'subscription'>(location?.state?.planData?.billingType === 2 ? 'subscription' : 'one-time');
  const [duration, setDuration] = useState<'monthly' | 'yearly'>(location?.state?.planData?.packageData?.duration === 2 ? 'monthly' : 'yearly');
  const [typeStart, setTypeStart] = useState<'now' | 'after'>('now');
  const billingFormSubmitRef = useRef<(() => void) | null>(null);
  const [billingInfoData, setBillingInfoData] = useState<InvoiceFormType | null>(null);

  const [cleanRequire, setCleanRequire] = useState<TCleanRequire>({
    chatbot: { needToClean: 0, cleaned: {}, code: {} },
    file: { needToClean: 0, cleaned: {}, code: {} },
    storage: { needToClean: 0, cleaned: {}, code: {} },
    web: { needToClean: 0, cleaned: {}, code: {} }
  });

  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  /* Queries */
  const { data: mainPackagesData, isLoading: isMainLoading } = useQuery({
    queryKey: ['getMainPackages'],
    queryFn: getMainPackagesService,
  });

  const { data: usageSummary } = useQuery(
    ['client-usage-summary'],
    () => getClientUsageSummary(),
    {
      onError: () => {
        message.error('Unable to load agent list. Please try again later.');
      }
    }
  );

  const { data: dataDiscount } = useQuery({
    queryKey: ['change-plan-discout'],
    queryFn: changePlanDiscountService
  });

  const handleSubmitDataStep1 = () => {
    setStep(2);
  };

  const handleActionBack = () => {
    if (cleanRequire.chatbot.needToClean <= 0
      && cleanRequire.file.needToClean <= 0
      && cleanRequire.storage.needToClean <= 0
      && cleanRequire.web.needToClean <= 0 && step === 4 && typeStart === 'now') {
      setStep(step - 2);
      return;
    }
    setStep(step - 1);
  };

  const handleCheckout = async () => {

  };

  /* Form */
  const method = useForm<IForm>({
    defaultValues: {
      mainPackage: null,
      addOnPackage: [],
    },
    mode: 'onChange',
  });

  const mainPackageSelected = useWatch({
    control: method.control,
    name: 'mainPackage',
  });

  const addOnValues = useWatch({
    control: method.control,
    name: 'addOnPackage',
  });

  /* Invoice info form */
  const invoiceInfoMethod = useForm<InvoiceFormType>({
    defaultValues: {
      invoiceType: 1,
      billingInfo: {
      }
    },
    resolver: yupResolver(changePlanBillingInfoSchema),
    mode: 'onChange'
  });

  const invoiceFormWatch = useWatch({ control: invoiceInfoMethod?.control });

  const { append } = useFieldArray({
    control: method.control,
    name: 'addOnPackage',
  });

  const total = (addOnValues?.reduce(
    (acc, pkg) => acc + (pkg.price ?? 0) * (billingType === 'subscription' ? 1 : pkg.quantity || 1) * (pkg.unit || 1),
    0
  ) ?? 0)
    + (mainPackageSelected?.price ?? 1)
    * (mainPackageSelected?.unit ?? 1)
    * (billingType === 'subscription' ? 1 : mainPackageSelected?.quantity ?? 0);

  function totalTiers() {
    const totals = {
      numberOfAgent: 0,
      numberOfFile: 0,
      numberOfSession: 0,
      numberOfWebsite: 0,
      totalSizeLimit: 0,
    };

    // Xử lý dữ liệu từ mảng: lặp qua từng phần tử trong mảng
    if (Array.isArray(addOnValues)) {
      // Kiểm tra xem dataArray có phải là một mảng hợp lệ không
      addOnValues.forEach((item) => {
        // Đảm bảo phần tử hiện tại và thuộc tính latestTier của nó tồn tại
        if (item) {
          totals.numberOfAgent
            += item.numberOfAgent * item.duration * (item.quantity ?? 1) || 0;
          totals.numberOfFile
            += item.numberOfFile * item.duration * (item.quantity ?? 1) || 0;
          totals.numberOfSession
            += item.numberOfSession * item.duration * (item.quantity ?? 1) || 0;
          totals.numberOfWebsite
            += item.numberOfWebsite * item.duration * (item.quantity ?? 1) || 0;
          totals.totalSizeLimit
            += item.totalSizeLimit * item.duration * (item.quantity ?? 1) || 0;
        }
      });
    }

    // Xử lý dữ liệu từ đối tượng riêng lẻ
    if (mainPackageSelected) {
      totals.numberOfAgent
        += mainPackageSelected.numberOfAgent
        * mainPackageSelected.duration * (mainPackageSelected.quantity ?? 1) || 0;
      totals.numberOfFile
        += mainPackageSelected.numberOfFile
        * mainPackageSelected.duration * (mainPackageSelected.quantity ?? 1) || 0;
      totals.numberOfSession
        += mainPackageSelected.numberOfSession
        * mainPackageSelected.duration * (mainPackageSelected.quantity ?? 1)
        || 0;
      totals.numberOfWebsite
        += mainPackageSelected.numberOfWebsite
        * mainPackageSelected.duration * (mainPackageSelected.quantity ?? 1)
        || 0;
      totals.totalSizeLimit
        += mainPackageSelected.totalSizeLimit
        * mainPackageSelected.duration * (mainPackageSelected.quantity ?? 1) || 0;
    }

    return totals;
  }

  const maxDuration = useMemo(() => {
    const arrDuration: number[] = addOnValues.map(
      (item) => item.quantity || 1
    ).concat(mainPackageSelected?.quantity || 1);
    const max = Math.max(...arrDuration);
    return max;
  }, [addOnValues, mainPackageSelected?.quantity]);

  const totalFileCleaned = useMemo(() => Object.values(cleanRequire.file.cleaned)
    .reduce((sum, value) => sum + value, 0), [cleanRequire.file.cleaned]);

  const totalWebCleaned = useMemo(() => Object.values(cleanRequire.web.cleaned)
    .reduce((sum, value) => sum + value, 0), [cleanRequire.web.cleaned]);

  const totalStorageCleaned = useMemo(() => Object.values(cleanRequire.storage.cleaned)
    .reduce((sum, value) => sum + value, 0), [cleanRequire.storage.cleaned]);

  const totalChatbotCleaned = useMemo(() => Object.values(cleanRequire.chatbot.cleaned)
    .reduce((sum, value) => sum + value, 0), [cleanRequire.chatbot.cleaned]);
  const handleAction = () => {
    if (step === 1) {
      if (billingType === 'one-time' && typeStart === 'now' && total - (dataDiscount?.discountAmount || 0) <= 0) {
        message.error('Invalid payment amount. Please check your order value');
        return;
      }
      handleSubmitDataStep1();
    } else if (step === 2) {
      invoiceInfoMethod.handleSubmit((data) => {
        setBillingInfoData({
          ...data,
          billingInfo: {
            ...data.billingInfo,
            invoiceEmails: data?.billingInfo.invoiceEmail,
          },
        });
        if (cleanRequire.chatbot.needToClean <= 0
          && cleanRequire.file.needToClean <= 0
          && cleanRequire.storage.needToClean <= 0
          && cleanRequire.web.needToClean <= 0 && typeStart === 'now') {
          setStep(4);
          return;
        }
        setStep(3);
      })();
    } else if (step === 3) {
      if ((cleanRequire.chatbot.needToClean > totalChatbotCleaned
        || cleanRequire.file.needToClean > totalFileCleaned
        || cleanRequire.storage.needToClean > totalStorageCleaned
        || cleanRequire.web.needToClean > totalWebCleaned) && typeStart === 'now') {
        message.error('Please clean your data before proceeding to meet the new plan’s quota.');
        return;
      }
      setStep(4);
    }
  };
  /* Effect */
  useEffect(() => {
    const mainPackageByName = mainPackagesData?.data
      ?.filter(
        (item) => item?.servicePackageData?.duration
          === location?.state?.planData?.packageData?.duration
      )
      ?.find((item) => item?.servicePackageData?.name === planName);
    if (mainPackageByName) {
      method.setValue(
        'mainPackage',
        { ...mainPackageByName.servicePackageData, unit: 1, quantity: 1 },
        { shouldDirty: true, shouldValidate: true }
      );
    }
  }, [duration, mainPackagesData, method, planName]);

  useEffect(() => {
    if (location?.state?.invoiceData && !billingInfoData?.billingInfo) {
      setBillingInfoData({
        billingInfo: {
          ...location?.state?.invoiceData,
          taxId:
            location.state.invoiceData?.taxCode,
          fullName: location.state.invoiceData?.companyName,
          nationalId: location.state.invoiceData?.taxCode,
        },
        invoiceType: location.state.invoiceData.invoiceType
      });
      invoiceInfoMethod.reset({
        invoiceType: 2,
        billingInfo: {
          ...location?.state?.invoiceData,
          taxId: location.state.invoiceData?.taxCode,
          fullName: location.state.invoiceData?.companyName,
          nationalId: location.state.invoiceData?.taxCode,
        }
      });
    }
  }, [billingInfoData?.billingInfo, invoiceInfoMethod, location.state.invoiceData,
  location.state?.planData?.billingType, location.state?.planData?.packageData?.duration]);

  useEffect(() => {
    if (usageSummary && mainPackageSelected) {
      // tính tổng giới hạn chatbot
      const addonAgents = Array.isArray(addOnValues) ? addOnValues.reduce(
        (sum, item) => sum + ((item?.numberOfAgent ?? 0) * (item?.unit ?? 1)),
        0
      ) : 0;
      const chatbotNedClean = usageSummary.ttlAi
        - (mainPackageSelected.numberOfAgent + addonAgents);

      const chatbotClean: TClean = {
        ...cleanRequire.chatbot,
        needToClean: chatbotNedClean > 0
          ? chatbotNedClean : 0,
        code: location.state?.dataRetry?.reqDelData
          ?.agentCodes || []
      };

      // tính tổng giới hạn storage
      const addonStorage = Array.isArray(addOnValues) ? addOnValues.reduce(
        (sum, item) => sum + ((item?.totalSizeLimit ?? 0) * (item?.unit ?? 1)),
        0
      ) : 0;

      const storageNeedClean = usageSummary.ttlKnowledge
        - (mainPackageSelected.totalSizeLimit + addonStorage);

      const storageClean: TClean = {
        ...cleanRequire.storage,
        needToClean: (() => {
          if (storageNeedClean <= 0) return 0;
          return Math.round(storageNeedClean * 100) / 100;
        })(),

      };

      // tính tổng giới hạn website
      const addonWeb = Array.isArray(addOnValues) ? addOnValues.reduce(
        (sum, item) => sum + ((item?.numberOfWebsite ?? 0) * (item?.unit ?? 1)),
        0
      ) : 0;

      const webNeedClean = usageSummary.ttlWeb
        - (mainPackageSelected.numberOfWebsite + addonWeb);

      const webClean: TClean = {
        ...cleanRequire.web,
        needToClean: webNeedClean > 0
          ? webNeedClean : 0,

      };

      // Tính tổng số lượng file
      const addonFile = Array.isArray(addOnValues) ? addOnValues.reduce(
        (sum, item) => sum + ((item?.numberOfFile ?? 0) * (item?.unit ?? 1)),
        0
      ) : 0;
      const fileNeedClean = usageSummary.ttlFile
        - (mainPackageSelected.numberOfFile + addonFile);

      const fileClean: TClean = {
        ...cleanRequire.file,
        needToClean: fileNeedClean > 0
          ? fileNeedClean : 0,
        code: location.state?.dataRetry?.reqDelData
          ?.documentCodes || []
      };
      setCleanRequire({
        chatbot: chatbotClean, file: fileClean, storage: storageClean, web: webClean
      });
    }
  }, [mainPackageSelected, setCleanRequire,
    usageSummary, addOnValues, location.state?.dataRetry?.reqDelData?.agentCodes,
    location.state?.dataRetry?.reqDelData?.documentCodes,
  ]);

  useEffect(() => {
    if (location.state.dataRetry) {
      setTypeStart(location.state?.dataRetry?.switchType === 2 ? 'after' : 'now');
      setDuration(location.state.dataRetry?.packageData?.duration === 3 ? 'yearly' : 'monthly');

      const mainPkg = mainPackagesData?.data.find(
        (item) => item.servicePackageData.id
          === location.state?.dataRetry?.packageData?.servicePackageId
      );
      if (mainPkg) {
        method.setValue('mainPackage', { ...mainPkg.servicePackageData, quantity: location.state.dataRetry?.packageData?.quantity });

        const result = mainPkg.servicePackageData.servicePackageAddOnData?.filter(
          (pkg) => location.state.dataRetry?.packageData?.servicePackageAddOns.some(
            (a: { servicePackageAddOnId: number }) => a.servicePackageAddOnId === pkg.id
          )
        ).map((pkg) => {
          const matchedA = location.state.dataRetry?.packageData?.servicePackageAddOns.find(
            (a: { servicePackageAddOnId: number }) => a.servicePackageAddOnId === pkg.id
          );
          return {
            ...pkg,
            quantity: matchedA.quantity,
            unit: matchedA.unit,
            packageId: pkg.id
          };
        });
        if (result) method.setValue('addOnPackage', result, { shouldDirty: true, shouldValidate: true });
      }
      setBillingType(location.state?.dataRetry?.packageData?.billingType === 2 ? 'subscription' : 'one-time');
      setBillingInfoData(
        {
          billingInfo: {
            ...location.state?.dataRetry?.invoiceData,
            taxId: location.state?.dataRetry?.invoiceData?.taxCode,
            invoiceEmail: location.state?.dataRetry?.invoiceData?.invoiceEmails
          },
          invoiceType: location.state?.dataRetry?.invoiceData?.invoiceType
        }
      );
      invoiceInfoMethod.reset({
        billingInfo: {
          ...location.state?.dataRetry?.invoiceData,
          taxId: location.state?.dataRetry?.invoiceData?.taxCode,
          invoiceEmail: location.state?.dataRetry?.invoiceData?.invoiceEmails
        },
        invoiceType: location.state?.dataRetry?.invoiceData?.invoiceType
      });

      setStep(1);
    }
  }, [invoiceInfoMethod, location.state.dataRetry,
    location.state.invoiceData, mainPackagesData?.data, method]);
  return (
    <>
      <Section>
        <div className="p-checkout">
          <div className="p-checkout_steps">
            {[1, 2, 3, 4].map((val) => (
              <React.Fragment key={val}>
                <div
                  className={mapModifiers(
                    'p-checkout_steps_step',
                    step === val ? 'active' : ''
                  )}
                >
                  <div
                    className={mapModifiers(
                      'p-checkout_steps_number',
                      step > val ? 'active' : ''
                    )}
                  >
                    {step > val ? (
                      <Icon iconName="checkWhite" size="20" />
                    ) : (
                      <Typography.Text extendClasses="p-checkout_steps_numberText fs-20x24">
                        {val}
                      </Typography.Text>
                    )}
                  </div>
                  <Typography.Text extendClasses="p-checkout_steps_title fs-20x24">
                    {val === 1
                      ? 'Select New Plan'
                      : val === 2
                        ? 'Billing Information'
                        : val === 3
                          ? 'Data Cleanup'
                          : 'Confirmation'}
                  </Typography.Text>
                </div>
                {val < 4 && <div className="p-checkout_steps_divider" />}
              </React.Fragment>
            ))}
          </div>

          <div className="p-checkout_content">
            {step === 1 && (
              <>
                <ChoosePackage
                  billingType={billingType}
                  total={billingType === 'one-time' && typeStart === 'now' ? total - (dataDiscount?.discountAmount || 0) : total}
                  duration={duration}
                  mainPackageSelected={mainPackageSelected}
                  visiblePackages={addOnValues}
                  method={method}
                  dataDiscount={typeStart === 'now' ? dataDiscount : undefined}
                />

                <div className="p-checkout_billingType p-addNewPackage_mt32">
                  <Typography.Text
                    fontweight="600"
                    extendClasses="p-checkout_titleProduct fs-20x24"
                  >
                    Choose when to start your new plan
                  </Typography.Text>
                  <div className="p-checkout_billingType_options">
                    <Label className="p-checkout_billingType_option">
                      <Radio
                        size={20}
                        checked={typeStart === 'now'}
                        onChange={() => setTypeStart('now')}
                      />
                      <Typography.Text extendClasses="p-checkout_billingType_optionText">
                        Switch now
                      </Typography.Text>
                    </Label>
                    <Label className="p-checkout_billingType_option">
                      <Radio
                        size={20}
                        checked={typeStart === 'after'}
                        onChange={() => setTypeStart('after')}
                      />
                      <Typography.Text extendClasses="p-checkout_billingType_optionText">
                        Switch after expiration
                      </Typography.Text>
                    </Label>
                  </div>
                </div>

                <div className="p-checkout_billingType">
                  <Typography.Text
                    fontweight="600"
                    extendClasses="p-checkout_titleProduct fs-20x24"
                  >
                    Billing type
                  </Typography.Text>
                  <div className="p-checkout_billingType_options">
                    <Label className="p-checkout_billingType_option">
                      <Radio
                        size={20}
                        checked={billingType === 'one-time'}
                        onChange={() => setBillingType('one-time')}
                      />
                      <Typography.Text extendClasses="p-checkout_billingType_optionText">
                        One-time purchase
                        {' '}
                        <Tooltip
                          placement="bottomRight"
                          title="When you choose onetime purchase, the minimum duration for monthly packages is 3 months."
                        >
                          <span>
                            <QuestionCircleOutlined />
                          </span>
                        </Tooltip>
                      </Typography.Text>
                    </Label>
                    <Label className="p-checkout_billingType_option">
                      <Radio
                        size={20}
                        checked={billingType === 'subscription'}
                        onChange={() => setBillingType('subscription')}
                      // disabled={location.state?.planData?.billingType === 1}
                      // readOnly={location.state?.planData?.billingType === 1}
                      // extendClasses={
                      //   location.state?.planData?.billingType === 1
                      //     ? 'p-addNewPackage_radioDisable'
                      //     : ''
                      // }
                      />
                      <Typography.Text extendClasses="p-checkout_billingType_optionText">
                        Subscription
                      </Typography.Text>
                    </Label>
                  </div>
                </div>
                <PackageSelector
                  mainPackages={
                    mainPackagesData?.data
                      ?.filter(
                        (item) => item?.servicePackageData?.duration
                          === (duration === 'yearly' ? 3 : 2)
                      )
                      ?.map((item) => item.servicePackageData) || []
                  }
                  isAddonPackageLoading={false}
                  isMainPackageLoading={isMainLoading}
                  selectedMain={mainPackageSelected}
                  selectedAddons={addOnValues}
                  setSelectedAddons={(data) => {
                    const foundIndex = addOnValues.findIndex(
                      (item) => item.packageId === data?.packageId
                    );

                    if (foundIndex === -1) {
                      append(data);
                    } else {
                      method.setValue('addOnPackage', addOnValues.filter((item) => item?.packageId !== data?.packageId));
                    }
                  }}
                  duration={duration}
                  setDuration={setDuration}
                  method={method}
                  billingType={billingType}
                  currentPackage={location?.state?.planData?.packageData}
                  currentBillingType={location?.state.planData?.billingType}
                />
              </>
            )}

            {step === 2 && (
              <FormProvider {...invoiceInfoMethod}>
                <BillingInformation
                  triggerSubmitRef={billingFormSubmitRef}
                  initialData={billingInfoData || undefined}
                  method={invoiceInfoMethod}
                />
              </FormProvider>

            )}

            {step === 3 && (
              <CleanData
                cleanRequire={cleanRequire}
                setCleanRequire={setCleanRequire}
                typeSwitch={typeStart}
              />
            )}

            {step === 4 && (
              <Confirmation
                billingType={billingType}
                billingData={billingInfoData!}
                total={billingType === 'one-time' && typeStart === 'now' ? total - (dataDiscount?.discountAmount || 0) : total}
                duration={duration}
                mainPackageSelected={mainPackageSelected}
                visiblePackages={addOnValues}
                handleCheckout={handleCheckout}
                method={method}
                totalTiers={totalTiers()}
                maxDuration={maxDuration}
                cleanRequire={cleanRequire}
                typeStart={typeStart}
                addOnValues={addOnValues}
                invoiceFormWatch={invoiceFormWatch as InvoiceFormType}
                dataDiscount={typeStart === 'now' ? dataDiscount : undefined}
              />
            )}
          </div>
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey="6Lcp4MIZAAAAABHdGwULZhEv01tu2iODEDzSterA"
            size="invisible"
          />
        </div>
      </Section>
      <FooterChangePlan
        step={step}
        actionBack={() => handleActionBack()}
        action={handleAction}
      />
    </>
  );
};

export default ChangePlan;
